/**
 * Sign in.
 *
 * Two successful outcomes, and the second is not an error: an account still
 * holding an administrator-issued temporary password is signed in with a
 * restricted token and sent to /change-password. Only after that does the ERP
 * open to them.
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, ErrorAlert, Input, PasswordInput } from '../../components/index.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { useMutation } from '../../hooks/useApi.js';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '' });

  const { mutate, pending, error, fieldErrors } = useMutation(async () => {
    const result = await signIn(form);

    // First sign-in: the password has to be replaced before anything else.
    if (result.mustChangePassword) {
      navigate('/change-password', { replace: true });
      return;
    }

    navigate(location.state?.from?.pathname ?? '/', { replace: true });
  });

  const onSubmit = (event) => {
    event.preventDefault();
    mutate().catch(() => {
      // The error is already on the mutation state and rendered below.
    });
  };

  return (
    <Card>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <div>
          <h1 className="auth-title">Sign in</h1>
          <p className="auth-subtitle">Welcome back. Sign in to your organization&rsquo;s NexERP workspace.</p>
        </div>

        <ErrorAlert error={error} />

        <Input
          id="login-email"
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          autoFocus
          value={form.email}
          error={fieldErrors.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />

        <PasswordInput
          id="login-password"
          label="Password"
          name="password"
          autoComplete="current-password"
          required
          value={form.password}
          error={fieldErrors.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />

        <div className="row-between text-sm">
          <Link to="/forgot-password">Forgot your password?</Link>
        </div>

        <Button id="login-submit-button" type="submit" variant="primary" size="lg" block loading={pending}>
          Sign in
        </Button>

        {/*
          No "create an account" link: there is no self-registration. An
          organization and its owner are created by the platform team, and
          everybody else is added by their own organization's administrator.
        */}
        <p className="auth-footer">
          Need an account? Ask your organization administrator, or contact the platform team to have an
          organization set up.
        </p>
      </form>
    </Card>
  );
}
